'use client';

import { create } from 'zustand';
import type { ShowPopupOptions } from '../bootstrap/tmaActions';

export interface ConfirmDialogRequest {
  /** Monotonic id. The host answers by id, so a late Escape or a `popstate`
   *  can never settle a request that already got its answer. */
  id: number;
  options: ShowPopupOptions;
  resolve: (buttonId: string | null) => void;
}

interface ConfirmDialogState {
  /** Oldest first; only the head is on screen. `window.confirm` serialised
   *  calls by freezing the page — the queue does it without freezing. */
  queue: ConfirmDialogRequest[];
  /** Mounted hosts, oldest first. Only the first one draws: the legacy
   *  `/app` catch-all mounts TmaApp inside the web provider tree, so two
   *  hosts can be alive at once and would otherwise paint the same dialog
   *  twice. */
  hosts: symbol[];
  enqueue: (request: ConfirmDialogRequest) => void;
  drop: (id: number) => void;
  addHost: (token: symbol) => void;
  removeHost: (token: symbol) => void;
}

let nextRequestId = 1;

export const useConfirmDialogStore = create<ConfirmDialogState>((set) => ({
  queue: [],
  hosts: [],
  enqueue: (request) => set((s) => ({ queue: [...s.queue, request] })),
  drop: (id) => set((s) => ({ queue: s.queue.filter((r) => r.id !== id) })),
  addHost: (token) => set((s) => ({ hosts: [...s.hosts, token] })),
  removeHost: (token) => set((s) => ({ hosts: s.hosts.filter((t) => t !== token) })),
}));

/** Is a `ConfirmDialogHost` mounted to draw the request? */
export function hasConfirmDialogHost(): boolean {
  return useConfirmDialogStore.getState().hosts.length > 0;
}

/**
 * Show the in-app confirmation and resolve with the pressed button's answer.
 * Never rejects: a close past the buttons resolves too (see `dismissAnswer`).
 */
export function openConfirmDialog(options: ShowPopupOptions): Promise<string | null> {
  return new Promise((resolve) => {
    useConfirmDialogStore.getState().enqueue({ id: nextRequestId++, options, resolve });
  });
}
